/**
 * Staff-side API for the borrower portal request inbox — v2 servicing
 * (Slice 4 backend / Slice 6 cutover). Uses the staff apiService (auth
 * headers + tenant context handled there); each call stamps
 * X-Portal-Client-Version so the drain-window telemetry (A-1 clause 2) can
 * tell new-bundle traffic from old (old bundles send no such header).
 */

import { apiService } from './api';

const PORTAL_CLIENT_VERSION = process.env.REACT_APP_GIT_SHA || 'dev';
const portalHeaders = { headers: { 'X-Portal-Client-Version': PORTAL_CLIENT_VERSION } };

export type PortalRequestType = 'contact' | 'dispute' | 'promise_to_pay';
export type PortalRequestStatus = 'open' | 'in_review' | 'resolved' | 'closed';
export type PortalMessageAuthorType = 'borrower' | 'staff';
export type PortalPromiseDecision = 'pending' | 'approved' | 'declined';

export interface PortalMessage {
  id: string;
  author_type: PortalMessageAuthorType;
  body: string;
  created_at: string;
  is_backfilled: boolean;
}

export interface StaffPortalRequest {
  id: string;
  request_type: PortalRequestType;
  status: PortalRequestStatus;
  subject: string;
  message: string;
  account_id?: string | null;
  payment_id?: string | null;
  proposed_promise_date?: string | null;
  staff_response?: string | null;
  created_at: string;
  customer_name?: string | null;
  account_number?: string | null;
  promise_decision?: PortalPromiseDecision | null;
  last_message_at?: string | null;
  last_message_author?: PortalMessageAuthorType | null;
  last_message_id?: string | null;
  messages?: PortalMessage[];
  archived_by_staff_at?: string | null;
}

export interface StaffPortalRequestCounts {
  awaiting_staff: number;
  pending_promises: number;
}

export interface StaffRequestListFilters {
  awaiting_staff?: boolean;
  status?: PortalRequestStatus;
  request_type?: PortalRequestType;
  include_archived?: boolean;
  customer_id?: string;
  promise_decision?: PortalPromiseDecision;
  sort?: 'last_activity' | 'proposed_promise_date';
  skip?: number;
  limit?: number;
}

export interface StaffRequestUpdatePayload {
  status: PortalRequestStatus;
  promise_decision?: 'approved' | 'declined';
}

export const staffPortalRequestsApi = {
  async list(filters?: StaffRequestListFilters): Promise<StaffPortalRequest[]> {
    return apiService.get<StaffPortalRequest[]>('/customer-portal/staff/requests', {
      ...portalHeaders,
      params: filters,
    });
  },

  async counts(): Promise<StaffPortalRequestCounts> {
    return apiService.get<StaffPortalRequestCounts>('/customer-portal/staff/requests/counts', {
      ...portalHeaders,
    });
  },

  async getThread(id: string): Promise<StaffPortalRequest> {
    return apiService.get<StaffPortalRequest>(`/customer-portal/staff/requests/${id}`, {
      ...portalHeaders,
    });
  },

  async getMessages(id: string, before?: string, limit = 50): Promise<PortalMessage[]> {
    return apiService.get<PortalMessage[]>(`/customer-portal/staff/requests/${id}/messages`, {
      ...portalHeaders,
      params: { before, limit },
    });
  },

  async postMessage(id: string, body: string, clientMessageId: string): Promise<PortalMessage> {
    return apiService.post<PortalMessage>(
      `/customer-portal/staff/requests/${id}/messages`,
      { body, client_message_id: clientMessageId },
      portalHeaders
    );
  },

  async updateStatus(id: string, payload: StaffRequestUpdatePayload): Promise<StaffPortalRequest> {
    return apiService.put<StaffPortalRequest>(
      `/customer-portal/staff/requests/${id}`,
      payload,
      portalHeaders
    );
  },

  async archive(id: string): Promise<StaffPortalRequest> {
    return apiService.post<StaffPortalRequest>(
      `/customer-portal/staff/requests/${id}/archive`,
      undefined,
      portalHeaders
    );
  },

  async unarchive(id: string): Promise<StaffPortalRequest> {
    return apiService.post<StaffPortalRequest>(
      `/customer-portal/staff/requests/${id}/unarchive`,
      undefined,
      portalHeaders
    );
  },

  async listForCustomer(customerId: string): Promise<StaffPortalRequest[]> {
    return apiService.get<StaffPortalRequest[]>(
      `/customer-portal/staff/customers/${customerId}/requests`,
      { ...portalHeaders }
    );
  },
};
